import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { components } from '@greasemap/api-client';
import { ApiError } from '../api/ApiError';
import { useApiClient } from '../api/ApiClientProvider';
import { useAuth } from '../auth/AuthProvider';

/** The signed-in user, as returned by `GET /api/me`. */
export type CurrentUser = components['schemas']['CurrentUserResult'];

/** Query key for the current user, exported so mutations can invalidate it. */
export const CURRENT_USER_QUERY_KEY = ['currentUser'] as const;

/**
 * Loads the signed-in user.
 *
 * Every screen needs this and none of them should refetch it independently, which is exactly what
 * a shared query key buys: one request, cached, deduplicated across components.
 *
 * @returns The query result for the current user.
 */
export function useCurrentUser(): UseQueryResult<CurrentUser, ApiError> {
  const client = useApiClient();
  const { status } = useAuth();

  return useQuery<CurrentUser, ApiError>({
    queryKey: CURRENT_USER_QUERY_KEY,
    // Only ask for the current user once sign-in has resolved. Firing during the redirect/init window
    // sends the request before a token exists, caching a 401 the UI would then show as an error even
    // though the user is signing in. Anonymous callers never call it at all.
    enabled: status === 'authenticated',
    queryFn: async () => {
      const { data, error, response } = await client.GET('/api/me');

      if (error !== undefined || data === undefined) {
        throw new ApiError(
          response.status,
          response.status === 401
            ? 'You are not signed in.'
            : `The API returned ${response.status}.`,
        );
      }

      return data;
    },
  });
}
